import React, { useId, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  readRichText,
  richTextExcerpt
} from '../../../../common/richTextDocument';
import './styles.scss';
const renderNode = (node, key) => {
  if (node.type === 'text')
    return (
      <React.Fragment key={key}>
        {(node.marks || []).reduce((child, mark) => {
          if (mark.type === 'bold') return <strong>{child}</strong>;
          if (mark.type === 'italic') return <em>{child}</em>;
          if (mark.type === 'underline') return <u>{child}</u>;
          return (
            <a href={mark.attrs.href} target="_blank" rel="noopener noreferrer">
              {child}
            </a>
          );
        }, node.text)}
      </React.Fragment>
    );
  if (node.type === 'hardBreak') return <br key={key} />;
  const Tag = {
    paragraph: 'p',
    heading: node.attrs?.level === 1 ? 'h3' : 'h4',
    bulletList: 'ul',
    orderedList: 'ol',
    listItem: 'li'
  }[node.type];
  return (
    <Tag key={key}>
      {(node.content || []).map((child, index) => renderNode(child, index))}
    </Tag>
  );
};
const Article = ({ document }) => (
  <div className="rich-text-article">{document.content.map(renderNode)}</div>
);
Article.propTypes = { document: PropTypes.object.isRequired };
const RichTextValue = ({ value, label = 'Rich Text Field' }) => {
  const result = useMemo(() => readRichText(value), [value]);
  const [expanded, setExpanded] = useState(false);
  const [reading, setReading] = useState(false);
  const id = useId();
  if (result.error)
    return (
      <p className="rich-text-message" role="status">
        {result.error}
      </p>
    );
  if (!result.document) return <span className="rich-text-message">-</span>;
  const long = result.characters > 1000 || result.nodes > 40;
  return (
    <div className="rich-text-value">
      <div id={id}>
        <Article
          document={
            long && !expanded
              ? richTextExcerpt(result.document)
              : result.document
          }
        />
      </div>
      <div className="rich-text-actions">
        {long && (
          <Button
            type="button"
            size="small"
            aria-expanded={expanded}
            aria-controls={id}
            onClick={() => setExpanded(!expanded)}
          >
            {expanded ? 'Show less' : 'Show more'}
          </Button>
        )}
        <Button
          type="button"
          size="small"
          onClick={() => setReading(true)}
          aria-label={'Open reading view: ' + label}
        >
          Open reading view
        </Button>
      </div>
      <Dialog
        open={reading}
        onClose={() => setReading(false)}
        fullWidth
        maxWidth="md"
        scroll="paper"
        aria-labelledby={id + '-title'}
      >
        <DialogTitle id={id + '-title'}>{label}</DialogTitle>
        <DialogContent dividers>
          <Article document={result.document} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReading(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};
RichTextValue.propTypes = { value: PropTypes.string, label: PropTypes.string };
export default RichTextValue;
